import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { fetchArtworks } from "../../services/arthouseApi";
import GalleryFeedHeader from "../../components/ArtHouse/GalleryFeedHeader";
import ArtFilters from "../../components/ArtHouse/ArtFilters";
import ArtGrid from "../../components/ArtHouse/ArtGrid";
import { ShowcaseArtworkCard } from "../../components/ArtHouse/ArtworkCard";

function getArtworkId(a) {
  return a?.id ?? a?.artwork_id ?? a?.artworkId ?? null;
}

function safeArr(x) {
  return Array.isArray(x) ? x : [];
}

function getFullPath(loc) {
  return `${loc.pathname}${loc.search || ""}${loc.hash || ""}`;
}

export default function ArtHouseGalleryPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const location = useLocation();

  const [sort, setSort] = useState("new");
  const [q, setQ] = useState("");
  const [tag, setTag] = useState("");

  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({ hasMore: false });
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  const [dense, setDense] = useState(false);
  const [openingProfile, setOpeningProfile] = useState(false);

  const [showcaseTab, setShowcaseTab] = useState("new"); // kept
  const [showcaseView, setShowcaseView] = useState("masonry"); // "masonry" | "grid"

  // our own scroll container for the grid only
  const gridScrollerRef = useRef(null);
  // sentinel for infinite scroll
  const sentinelRef = useRef(null);

  // capture this page's full path (so creator profile can go Back to EXACTLY here)
  const galleryReturnPath = useMemo(() => getFullPath(location), [location]);

  // preserve hub state if we came from Hub
  const hubState = useMemo(() => {
    const incoming = location.state || {};
    return {
      ...incoming,

      // keep existing hub fields
      hubSection: incoming.hubSection || "arthouse",

      // normalize "from" for this page
      from: "arthouse-gallery",

      // ensure there is ALWAYS a back target for downstream pages
      // (creator profile / dashboard can read this as backTo/returnTo)
      backTo:
        incoming.backTo ||
        incoming.returnTo ||
        incoming.returnPath ||
        incoming.fromPath ||
        galleryReturnPath,
      returnTo:
        incoming.returnTo ||
        incoming.backTo ||
        incoming.returnPath ||
        incoming.fromPath ||
        galleryReturnPath,
      returnPath:
        incoming.returnPath ||
        incoming.backTo ||
        incoming.returnTo ||
        incoming.fromPath ||
        galleryReturnPath,
    };
  }, [location.state, galleryReturnPath]);

  // Read initial tag from URL query parameter
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlTag = params.get('tag');
    if (urlTag && urlTag !== tag) {
      setTag(urlTag);
    }
  }, [location.search]);

  // Update URL when tag changes
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlTag = params.get('tag');
    
    if (tag && tag !== urlTag) {
      params.set('tag', tag);
      nav(`${location.pathname}?${params.toString()}`, { replace: true, state: location.state });
    } else if (!tag && urlTag) {
      params.delete('tag');
      const newSearch = params.toString();
      nav(`${location.pathname}${newSearch ? `?${newSearch}` : ''}`, { replace: true, state: location.state });
    }
  }, [tag]);

  const hasMore = Boolean(pagination?.hasMore);

  const loadPage = useCallback(
    async (nextPage) => {
      if (loading) return;
      if (!hasMore && nextPage !== 1) return;

      setLoading(true);

      try {
        const data = await fetchArtworks({ sort, q, tag, page: nextPage, limit: 24 });
        const rawList = Array.isArray(data?.results) ? data.results : [];

        setItems((prev) => {
          if (nextPage === 1) return rawList;

          // Dedupe by artwork_id or id
          const seen = new Set(
            prev.map((p) => p?.id ?? p?.artwork_id ?? p?.artworkId)
          );
          const deduped = rawList.filter(
            (n) => !seen.has(n?.id ?? n?.artwork_id ?? n?.artworkId)
          );
          return [...prev, ...deduped];
        });

        setPagination(data?.pagination || { hasMore: false });
        setPage(nextPage);

        // reset scroll to top only on page 1
        if (nextPage === 1) {
          requestAnimationFrame(() => {
            if (gridScrollerRef.current) gridScrollerRef.current.scrollTop = 0;
          });
        }
      } catch (error) {
        if (nextPage === 1) {
          setItems([]);
          setPagination({ hasMore: false });
        }
      } finally {
        setLoading(false);
        setInitialLoading(false);
      }
    },
    [sort, q, tag, hasMore, loading]
  );

  // Load first page when filters change
  useEffect(() => {
    setPage(1);
    setItems([]);
    setPagination({ hasMore: false });
    loadPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sort, q, tag]);

  // Infinite scroll observer
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;

    const io = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry?.isIntersecting) return;
        if (loading) return;
        if (!hasMore) return;
        loadPage(page + 1);
      },
      {
        root: gridScrollerRef.current || null,
        rootMargin: "500px 0px",
        threshold: 0,
      }
    );

    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, loadPage, loading, page]);

  async function goToMyArtistProfile() {
    if (!user) return nav("/login");
    setOpeningProfile(true);
    try {
      // pass return path so dashboard/profile can "Back" to gallery too if you want
      nav("/dashboard/profile", {
        state: {
          ...hubState,
          from: "arthouse-gallery",
          backTo: galleryReturnPath,
        },
      });
    } finally {
      setOpeningProfile(false);
    }
  }

  const myProfileButtonLabel = openingProfile ? "Opening…" : "My Art Page";

  const showcaseItems = useMemo(() => {
    const list = safeArr(items);
    if (showcaseTab === "staff") {
      return list
        .slice()
        .sort(
          (a, b) => (Number(b?.likes || 0) || 0) - (Number(a?.likes || 0) || 0)
        );
    }
    return list;
  }, [items, showcaseTab]);

  return (
    // lock the page height; ONLY the grid scrolls
    <div className="h-screen overflow-hidden bg-transparent text-white">
      <div className="mx-auto h-full w-full max-w-[1400px] px-4 pt-6 pb-4 flex flex-col">
        {/* header/filters never scroll away */}
        <div className="shrink-0">
          <GalleryFeedHeader
            profileButtonLabel={myProfileButtonLabel}
            onProfileClick={goToMyArtistProfile}
            profileDisabled={openingProfile}
            q={q}
            setQ={setQ}
            dense={dense}
            setDense={setDense}
            showcaseView={showcaseView}
            setShowcaseView={setShowcaseView}
          />

          <div className="mt-3 sm:mt-4">
            <ArtFilters
              sort={sort}
              setSort={setSort}
              q={q}
              setQ={setQ}
              tag={tag}
              setTag={setTag}
            />
          </div>
        </div>

        {/* GRID SCROLLER (scrollbar hidden via your existing class) */}
        <div
          ref={gridScrollerRef}
          className="mt-6 flex-1 overflow-y-auto scrollbar-hide"
        >
          {initialLoading ? (
            <div className="rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl p-6 text-white/70">
              Loading…
            </div>
          ) : showcaseItems.length === 0 ? (
            <div className="rounded-2xl border border-white/10 bg-white/[0.06] backdrop-blur-xl p-6">
              <div className="text-lg font-semibold">No artwork found</div>
              <p className="mt-2 opacity-70">
                Try a different search, remove filters, or upload the first
                piece.
              </p>
              <div className="mt-4 flex items-center gap-2">
                <Link
                  to="/dashboard/art/new"
                  state={hubState}
                  className="inline-flex items-center justify-center rounded-2xl border border-sky-300/25 bg-sky-300/10 hover:bg-sky-300/15 px-4 py-2 text-sm font-semibold text-sky-200 transition"
                >
                  Upload Artwork
                </Link>
                <button
                  className="inline-flex items-center justify-center rounded-2xl border border-white/10 bg-white/[0.06] hover:bg-white/[0.09] px-4 py-2 text-sm font-semibold text-white/80 transition"
                  onClick={() => {
                    setSort("new");
                    setQ("");
                    setTag("");
                  }}
                >
                  Clear Search/Filters
                </button>
              </div>
            </div>
          ) : (
            <div className="relative bg-transparent overflow-visible pb-10">
              {showcaseView === "masonry" ? (
                <div className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-5 [column-fill:_balance]">
                  {/* show ALL returned items (no slice) */}
                  {showcaseItems.map((a, idx) => {
                    const artId = getArtworkId(a);
                    const key = artId != null ? String(artId) : `show-${idx}`;

                    return (
                      <div
                        key={key}
                        className="mb-5 break-inside-avoid transform-gpu transition hover:-translate-y-[2px]"
                      >
                        <div className="relative">
                          {/* pass state that CreatorTopBar expects */}
                          <ShowcaseArtworkCard
                            artwork={a}
                            hubState={{
                              ...hubState,
                              from: "arthouse-gallery",
                              backTo: galleryReturnPath,
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="overflow-visible">
                  {/* no limit prop so it can render everything */}
                  <ArtGrid
                    artworks={showcaseItems}
                    dense={dense}
                    noWrapper
                    hubState={{
                      ...hubState,
                      from: "arthouse-gallery",
                      backTo: galleryReturnPath,
                    }}
                    columns={{ base: 1, sm: 2, lg: 3 }}
                  />
                </div>
              )}

              {/* Infinite scroll sentinel */}
              <div
                ref={sentinelRef}
                className="h-20 flex items-center justify-center"
              >
                {loading && (
                  <div className="text-white/50 text-sm">Loading more...</div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
